<template>
    <div>
        <rkm-table
            :fields="fields"
            :items="data.model.atendimentos"
            :tbody-tr-class="rowClassPaciente"
            ref="RkmTable"
            responsive
            sort-by="horarioAgendado"
        >
            <template v-slot:cell(tempoEspera)="{item}">
                <rkm-icon
                    v-if="item.tempoEspera.id"
                    :id="uniqueId('tempo-espera-tooltip')"
                    :style="'color: ' + item.tempoEspera.cor"
                    :title="formatTempoEspera(item.tempoEspera.total)"
                    font-scale="2"
                    icon="battery-full"
                    v-rkm-tooltip.hover
                />

                <span v-else>
                    -
                </span>
            </template>

            <template v-slot:cell(pacienteNome)="{index, item}">
                <template v-if="item.pacienteNome">
                    <rkm-button
                        :id="uniqueId(`paciente-icon-${index}`)"
                        class="mr-2"
                        small
                    >
                        <rkm-icon icon="user" />
                    </rkm-button>

                    <rkm-popover
                        :target="uniqueId(`paciente-icon-${index}`)"
                        placement="top"
                        title="Dados cadastrais"
                        triggers="hover"
                    >
                        <div>
                            <span>
                                <strong>Data de nascimento:</strong> {{item.pacienteDataNascimento}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>Nome da mãe:</strong> {{item.pacienteNomeMae}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>CPF:</strong> {{item.pacienteCpf}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>CNS:</strong> {{item.pacienteCns}}
                            </span>
                        </div>
                    </rkm-popover>

                    <template>
                        {{item.pacienteNome}}

                        <template v-if="item.pacientePreferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            <strong>
                                ({{item.pacienteNomeSocial}})
                            </strong>
                        </template>

                        <span
                            v-if="item.pacienteObitoEm"
                            class="ml-1"
                            title="Paciente em óbito"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="cross" />
                        </span>

                        <span
                            v-if="item.pacienteBloqueado"
                            class="ml-1"
                            title="Cadastro bloqueado"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="ban" />
                        </span>

                        <span
                            v-if="isProvisorioOuTemporario(item)"
                            class="ml-1"
                            title="Paciente provisório/temporário"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="hourglass-half" />
                        </span>

                        <span
                            v-if="isNaoMunicipe(item)"
                            class="ml-1"
                            title="Paciente não munícipe"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="map-pin" />
                        </span>

                        <span
                            v-if="isSemCpfCns(item)"
                            class="ml-1"
                            title="Paciente sem CPF e CNS"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="exclamation-triangle" />
                        </span>

                        <span
                            v-if="item.pacienteResponsavelFamiliar"
                            class="ml-1"
                            title="Responsável familiar"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="crown" />
                        </span>

                        <span
                            v-if="isIdoso(item)"
                            class="ml-1"
                            title="Idoso"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="blind" />
                        </span>

                        <span
                            v-if="hasTea(item)"
                            class="ml-1"
                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="infinity" />
                        </span>

                        <span
                            v-if="hasPcd(item)"
                            class="ml-1"
                            title="Pessoa com Deficiência - PCD"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="wheelchair" />
                        </span>
                    </template>

                    <span
                        v-if="item.pacienteObitoEm"
                        class="ml-1"
                        title="Paciente em óbito"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="cross" />
                    </span>

                    <span
                        v-if="item.pacienteBloqueado"
                        class="ml-1"
                        title="Cadastro bloqueado"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="ban" />
                    </span>

                    <span
                        v-if="isProvisorioOuTemporario(item)"
                        class="ml-1"
                        title="Paciente provisório/temporário"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="hourglass-half" />
                    </span>

                    <span
                        v-if="item.pacienteClassificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                        class="ml-1"
                        title="Paciente não munícipe"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="map-pin" />
                    </span>

                    <span
                        v-if="!item.pacienteCpf && !item.pacienteCns"
                        class="ml-1"
                        title="Paciente sem CPF e CNS"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="exclamation-triangle" />
                    </span>

                    <span
                        v-if="item.pacienteResponsavelFamiliar"
                        class="ml-1"
                        title="Responsável familiar"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="crown" />
                    </span>

                    <span
                        v-if="parseInt(item.pacienteIdadeAnos) >= 60"
                        class="ml-1"
                        title="Idoso"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="blind" />
                    </span>

                    <span
                        v-if="hasTea(item)"
                        class="ml-1"
                        title="Pessoa com Transtorno do Espectro Autista - TEA"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="infinity" />
                    </span>

                    <span
                        v-if="hasPcd(item)"
                        class="ml-1"
                        title="Pessoa com Deficiência - PCD"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="wheelchair" />
                    </span>
                </template>
            </template>

            <template v-slot:cell(actions)="{item}">
                <rkm-button
                    @click="$refs.ModalChegada.open(item.id)"
                    color="primary"
                    small
                    title="Editar chegada"
                >
                    <rkm-icon icon="pen" />
                </rkm-button>

                <rkm-button
                    @click="$refs.ModalDeclaracaoComparecimento.open(item.id, {allowsToPrint: true})"
                    color="info"
                    small
                    title="Gerar declaração de comparecimento"
                >
                    <rkm-icon icon="file-medical" />
                </rkm-button>

                <rkm-button
                    :disabled="!item.podeDesfazerChegada"
                    @click="destroy(item.id)"
                    color="danger"
                    small
                    title="Desfazer chegada"
                >
                    <rkm-icon icon="undo" />
                </rkm-button>
            </template>
        </rkm-table>

        <ModalChegada
            @saved="$emit('saved')"
            ref="ModalChegada"
        />

        <ModalDeclaracaoComparecimento
            @saved="$emit('saved')"
            ref="ModalDeclaracaoComparecimento"
        />
    </div>
</template>

<script>
// Components.
import ModalChegada from './components/chegada/Modal'
import ModalDeclaracaoComparecimento from '@/components/ambulatorio/components/declaracao-comparecimento/Modal'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Mixins.
import {error, repository} from '@/mixins'

export default {
    components: {
        ModalChegada,
        ModalDeclaracaoComparecimento,
    },
    created() {
        this.repositories = {
            recepcao: this.getConectaSusRepository('ambulatorio.recepcao'),
        }
    },
    data: () => ({
        enums: {preferenciaNome, classificacao, subclassificacao, deficienciaBase, deficiencia},
        fields: [
            {key: 'tempoEspera', label: 'Tempo de espera'},
            {key: 'senha', label: 'Senha', sortable: true},
            {key: 'pacienteNome', label: 'Nome', sortable: true},
            {key: 'pacienteIdadeAnos', label: 'Idade', sortable: true},
            {key: 'pacienteProntuario', label: 'Prontuário', sortable: true},
            {key: 'pacienteSexoDescricao', label: 'Sexo', sortable: true},
            {key: 'especialidadeNome', label: 'Especialidade', sortable: true},
            {key: 'usuarioNome', label: 'Profissional', sortable: true},
            {key: 'horarioChegada', label: 'Horário de chegada', sortable: true},
            {key: 'horarioAgendado', label: 'Horário agendado', sortable: true},
            {key: 'setorNome', label: 'Setor atual', sortable: true},
            {key: 'filaStatusNome', label: 'Status', sortable: true},
            {class: 'col-min text-center', key: 'actions', label: 'Ações'},
        ],
    }),
    methods: {
        formatTempoEspera(value) {
            let format = 'Esperando por'

            if (value.dias !== null) {
                format = `${format} ${value.dias} dia(s)`
            }

            if (value.horas !== null) {
                format = `${format} ${value.horas} hora(s)`
            }

            if (value.minutos !== null) {
                if (value.horas !== null) {
                    format = `${format} e ${value.minutos} minuto(s)`
                } else {
                    format = `${format} ${value.minutos} minuto(s)`
                }
            }

            return format
        },
        isProvisorioOuTemporario(item) {

            return item.pacienteClassificacaoId == this.enums.classificacao.PROVISORIO
                || item.pacienteSubclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isNaoMunicipe(item) {

            return item.pacienteClassificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO
        },
        isSemCpfCns(item) {

            return !item.pacienteCpf && !item.pacienteCns
        },
        isIdoso(item) {

            return parseInt(item.pacienteIdadeAnos) >= 60
        },
        getDeficienciasBaseIds(item) {

            return item.pacienteDeficienciasBaseIds
                ? item.pacienteDeficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea(item) {

            return this.getDeficienciasBaseIds(item).includes(this.enums.deficienciaBase.TEA)
                || item.pacienteDeficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd(item) {

            return this.getDeficienciasBaseIds(item).some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!item.pacienteDeficienciaId && item.pacienteDeficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        rowClassPaciente(item, type) {
            if (item && type === 'row') {
                if (item.pacienteObitoEm) {
                    return 'table-purple'
                }
                if (item.pacienteBloqueado) {
                    return 'table-danger'
                }
                if (this.isProvisorioOuTemporario(item)) {
                    return 'table-orange'
                }
                if (this.isNaoMunicipe(item)) {
                    return 'table-brown'
                }
                if (this.isSemCpfCns(item)) {
                    return 'table-warning'
                }
            }
        },
        destroy(atendimentoId) {
            const destroy = () => this.repositories.recepcao.destroy(atendimentoId)
                .then(() => this.$emit('saved'))
                .catch(this.handleError)

            this.showDeleteConfirmation('Deseja realmente desfazer a chegada do paciente?', destroy)
        },
    },
    mixins: [error, repository],
    props: {
        data: {type: Object, required: true},
    },
}
</script>